/**
 * A escolha do modo claro/escuro nos cascos: Claro, Escuro ou Sistema (o `system` do
 * `TemaProvider`), como na conta e nos portais do aluno e do encarregado. Sem escolha guardada
 * segue o aparelho; um botão que só alternava entre claro e escuro gravava a escolha ao primeiro
 * toque e não deixava voltar a seguir o aparelho.
 */
import React from "react";
import { Check, Monitor, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import { BotaoIcone } from "../base";
import { ItemMenu, Menu } from "../sobreposicoes";
import type { Tema } from "../tema";

/** O que o casco recebe para mostrar e mudar o tema (o `useTema` do kit). */
export interface EscolhaDoTemaProps {
    /** O que está escolhido (`system` sem escolha guardada). */
    escolhido: Tema;
    /** O que se vê agora (com `system`, o do aparelho). */
    escuro: boolean;
    mudar: (tema: Tema) => void;
}

const TEMAS: { valor: Tema; rotulo: string; Icone: typeof Sun }[] = [
    { valor: "light", rotulo: "Claro", Icone: Sun },
    { valor: "dark", rotulo: "Escuro", Icone: Moon },
    { valor: "system", rotulo: "Sistema", Icone: Monitor },
];

/** Os três temas como itens de um `Menu`, com o escolhido marcado (o primeiro leva o título «Tema»). */
export function itensDoTema({ escolhido, mudar }: Pick<EscolhaDoTemaProps, "escolhido" | "mudar">, separadorAntes = false): ItemMenu[] {
    return TEMAS.map(({ valor, rotulo, Icone }, i) => ({
        chave: `tema-${valor}`,
        titulo: i === 0 ? "Tema" : undefined,
        separadorAntes: i === 0 && separadorAntes,
        icone: <Icone />,
        rotulo: (
            <span className="flex flex-1 items-center justify-between gap-3">
                {rotulo}
                {escolhido === valor ? <Check className="text-portal dark:text-portal-claro" aria-label="escolhido" /> : <span className="size-4" aria-hidden />}
            </span>
        ),
        onSelect: () => mudar(valor),
    }));
}

/** O botão da barra de cima: o ícone do que se vê agora e, ao tocar, os três temas. */
export function BotaoTema({ escolhido, escuro, mudar, className }: EscolhaDoTemaProps & { className?: string }) {
    const Icone = escolhido === "system" ? Monitor : escuro ? Moon : Sun;
    return (
        <Menu
            largura={220}
            gatilho={
                <BotaoIcone rotulo="Tema" tamanho="lg" className={className} data-tour="tema">
                    <Icone className="size-5" />
                </BotaoIcone>
            }
            itens={itensDoTema({ escolhido, mudar })}
        />
    );
}

/** Os três temas lado a lado (o rodapé da gaveta do telemóvel): um toque e está escolhido. */
export function TemaSegmentado({ escolhido, mudar, className }: Pick<EscolhaDoTemaProps, "escolhido" | "mudar"> & { className?: string }) {
    return (
        <div role="radiogroup" aria-label="Tema" className={cn("grid grid-cols-3 gap-1 rounded-lg bg-zinc-100 p-1 dark:bg-white/5", className)}>
            {TEMAS.map(({ valor, rotulo, Icone }) => {
                const activo = escolhido === valor;
                return (
                    <button
                        key={valor}
                        type="button"
                        role="radio"
                        aria-checked={activo}
                        onClick={() => mudar(valor)}
                        className={cn(
                            "flex min-h-11 min-w-0 items-center justify-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-portal/40",
                            activo
                                ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-white"
                                : "text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white",
                        )}
                    >
                        <Icone className="size-4 shrink-0" />
                        <span className="truncate">{rotulo}</span>
                    </button>
                );
            })}
        </div>
    );
}
